import type { ReactNode } from 'react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/cn'

interface Props {
  /** Conteúdo do gatilho — pode ser um ícone, badge, número, etc. */
  trigger: ReactNode
  /** Conteúdo do popover. */
  children: ReactNode
  /** Largura mínima do painel. */
  minWidth?: number
  /** Largura máxima do painel. */
  maxWidth?: number
  /** Alinhamento horizontal relativo ao gatilho. */
  align?: 'left' | 'right' | 'center'
  /** Aria-label do botão gatilho. */
  ariaLabel?: string
  /** Classe extra do botão gatilho. */
  triggerClassName?: string
  /** Classe extra do painel. */
  panelClassName?: string
  /** Cabeçalho opcional renderizado no topo. */
  title?: ReactNode
}

/**
 * Popover genérico para exibir informações detalhadas a partir de um ícone,
 * badge ou número em uma célula de tabela.
 *
 * Renderiza o painel via React Portal com `position: fixed` calculado a partir
 * do `getBoundingClientRect()` do gatilho — escapa de qualquer ancestral com
 * `overflow: hidden | auto` (DataTable, etc.).
 *
 * - Auto-flip vertical: abre acima se não couber abaixo.
 * - Reposiciona em scroll/resize.
 * - Fecha em clique fora, ESC ou scroll.
 */
export function InfoPopover({
  trigger,
  children,
  minWidth = 240,
  maxWidth = 360,
  align = 'right',
  ariaLabel = 'Mais informações',
  triggerClassName,
  panelClassName,
  title,
}: Props) {
  const [open, setOpen] = useState(false)
  const btnRef = useRef<HTMLButtonElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const [pos, setPos] = useState<{ top: number; left: number; flipUp: boolean }>({
    top: 0, left: 0, flipUp: false,
  })

  const recalc = () => {
    const btn = btnRef.current
    if (!btn) return
    const rect = btn.getBoundingClientRect()
    const panelH = panelRef.current?.offsetHeight ?? 160
    const panelW = panelRef.current?.offsetWidth ?? minWidth

    const spaceBelow = window.innerHeight - rect.bottom
    const flipUp = spaceBelow < panelH + 12 && rect.top > panelH + 12

    const top = flipUp ? rect.top - panelH - 6 : rect.bottom + 6

    let left: number
    if (align === 'right')      left = Math.max(8, rect.right - panelW)
    else if (align === 'center') left = Math.max(8, rect.left + rect.width / 2 - panelW / 2)
    else                         left = Math.min(window.innerWidth - panelW - 8, rect.left)

    // Garante que não vaze à direita
    left = Math.min(left, window.innerWidth - panelW - 8)

    setPos({ top, left, flipUp })
  }

  useLayoutEffect(() => {
    if (!open) return
    recalc()
    const t = requestAnimationFrame(recalc)
    return () => cancelAnimationFrame(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, align])

  useEffect(() => {
    if (!open) return
    const onScroll = () => setOpen(false)
    const onResize = () => recalc()
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (btnRef.current?.contains(target)) return
      if (panelRef.current?.contains(target)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onResize)
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onResize)
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o) }}
        className={cn('cursor-pointer inline-flex items-center justify-center', triggerClassName)}
      >
        {trigger}
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          role="dialog"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            minWidth,
            maxWidth,
            zIndex: 9999,
          }}
          className={cn(
            'bg-white ring-1 ring-gray-200 rounded-md text-[13px] text-gray-700',
            panelClassName,
          )}
        >
          {title && (
            <div className="px-3 py-2 border-b border-gray-100 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              {title}
            </div>
          )}
          <div className="px-3 py-2">{children}</div>
        </div>,
        document.body,
      )}
    </>
  )
}
